import type { PlanOption, SubscriptionStatus } from '@/services/api/settings'

export type PaywallContext =
  | 'missing_subscription'
  | 'trial_expired'
  | 'paid_expired'
  | 'paid_grace'
  | 'unknown'

export interface PaywallPresentation {
  kicker: string
  title: string
  message: string
  cta: string
}

export const GRACE_BANNER_DISMISSED_KEY = 'beoflow.subscription.graceBannerDismissed'

/** Where to send someone once Stripe hands them back.
 *
 *  The backend hardcodes `success_url` to /paywall and honours no returnTo, so
 *  /paywall reads this key and forwards. Three surfaces have to agree on the
 *  spelling — usePaywall writes and reads it, the athlete onboarding paywall
 *  writes it, and so does the coach wall — and a typo in any one of them
 *  strands a paying user on the recovery paywall rather than back where they
 *  were. One definition, so that cannot happen silently. */
export const PAYWALL_RETURN_KEY = 'beoflow.paywall.returnTo'

export function dismissGraceBannerForSession(): void {
  try { sessionStorage.setItem(GRACE_BANNER_DISMISSED_KEY, '1') } catch { /* storage unavailable */ }
}

const PAYWALL_PRESENTATIONS: Record<PaywallContext, PaywallPresentation> = {
  missing_subscription: {
    kicker: 'BEOFLOW PERFORMANCE',
    title: 'Unlock your plan',
    message:
      'Your training setup is ready. Choose a plan to turn your data into daily decisions and keep your plan moving.',
    cta: 'CONTINUE TO STRIPE',
  },
  trial_expired: {
    kicker: 'TRIAL ENDED',
    title: 'You set your goals. This is where they get earned.',
    message:
      'Your baseline is built. Unlock Beoflow to keep your training history, daily signal, and plan working together.',
    cta: 'UNLOCK BEOFLOW',
  },
  paid_expired: {
    kicker: 'ACCESS REQUIRED',
    title: 'Renew your access',
    message:
      'Your subscription ended, but your plan and history are still here. Renew to pick up exactly where you left off.',
    cta: 'RENEW BEOFLOW',
  },
  paid_grace: {
    kicker: 'GRACE ACCESS',
    title: 'Keep Beoflow flowing',
    message:
      'You still have full access. Renew before the grace period ends so your training flow does not pause.',
    cta: 'RENEW BEOFLOW',
  },
  unknown: {
    kicker: 'SUBSCRIPTION REQUIRED',
    title: 'Keep your training moving',
    message:
      'Your plan and history are preserved. Confirm your subscription to continue using Beoflow.',
    cta: 'RENEW BEOFLOW',
  },
}

export function isSubscriptionPaymentRequired(
  subscription: Pick<SubscriptionStatus, 'is_active' | 'requires_payment'> | null | undefined,
): boolean {
  if (!subscription) return false
  if (typeof subscription.requires_payment === 'boolean') {
    return subscription.requires_payment
  }
  return subscription.is_active === false
}

export function paywallContextFor(
  subscription: Pick<
    SubscriptionStatus,
    'access_state' | 'is_in_grace_period' | 'payment_required_reason'
  > | null | undefined,
): PaywallContext {
  if (
    subscription?.access_state === 'paid_grace' ||
    subscription?.is_in_grace_period
  ) {
    return 'paid_grace'
  }
  const reason = subscription?.payment_required_reason
  if (
    reason === 'missing_subscription' ||
    reason === 'trial_expired' ||
    reason === 'paid_expired'
  ) {
    return reason
  }
  return 'unknown'
}

export function paywallPresentationFor(context: PaywallContext): PaywallPresentation {
  return PAYWALL_PRESENTATIONS[context]
}

export function yearlyFirst(plans: PlanOption[]): PlanOption[] {
  return [...plans].sort((a, b) => {
    if (a.interval_key === b.interval_key) return 0
    return a.interval_key === 'yearly' ? -1 : 1
  })
}

export function annualSavingsPercent(plans: PlanOption[]): number | null {
  const monthly = plans.find((plan) => plan.interval_key === 'monthly')
  const yearly = plans.find((plan) => plan.interval_key === 'yearly')
  if (!monthly || !yearly || monthly.unit_amount <= 0) return null
  const fullYear = monthly.unit_amount * 12
  if (yearly.unit_amount >= fullYear) return null
  return Math.round((1 - yearly.unit_amount / fullYear) * 100)
}

export function safePaywallReturnPath(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/calendar'
  if (value === '/paywall' || value.startsWith('/paywall?')) return '/calendar'
  return value
}
